import { supabase } from "../supabase";
import { PAGE_SIZE, type TimelineItem } from "./timeline";

/** お気に入り一覧のページ送り。追加した日時(とタイブレークの media_id)で進める */
export interface FavoritesCursor {
  createdAt: string;
  mediaId: string;
}

export interface FavoritesPage {
  items: TimelineItem[];
  next: FavoritesCursor | null;
}

/**
 * お気に入り一覧を1ページ取得する。並びは「お気に入りに追加した順」の新しい順。
 * タイムライン(撮影日時順)とは意味が違うため、問い合わせを分けている。
 * RLS が自分の行しか返さないので、誰のものかの指定は不要。
 */
export async function fetchFavoritesPage(cursor: FavoritesCursor | null): Promise<FavoritesPage> {
  let query = supabase
    .from("media_favorites")
    .select(
      "created_at, media_id, media!inner(id, type, thumbnail_key, storage_key, duration_seconds, taken_at, uploader_id)",
    )
    .is("media.deleted_at", null)
    .order("created_at", { ascending: false })
    .order("media_id", { ascending: false })
    .limit(PAGE_SIZE);

  if (cursor !== null) {
    // (created_at, media_id) の組で比較する。同時刻の登録があっても取りこぼさない
    query = query.or(
      `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},media_id.lt.${cursor.mediaId})`,
    );
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = data as unknown as { created_at: string; media_id: string; media: TimelineItem }[];
  const last = rows.at(-1);
  return {
    items: rows.map((row) => row.media),
    next:
      rows.length === PAGE_SIZE && last !== undefined
        ? { createdAt: last.created_at, mediaId: last.media_id }
        : null,
  };
}

/**
 * お気に入り。
 *
 * **各自が自分のぶんだけを持つ**(→ 20260813000006_media_favorites.sql)。
 * RLS が自分の行しか返さないので、問い合わせ側で誰のものかを指定する必要はない。
 */
export async function fetchFavoriteIds(): Promise<Set<string>> {
  const { data, error } = await supabase.from("media_favorites").select("media_id");
  if (error) throw error;
  const rows: { media_id: string }[] = data;
  return new Set(rows.map((row) => row.media_id));
}

/**
 * 渡したうち、二人のどちらかがお気に入りに入れているもの。
 *
 * 自分のぶんしか見えない RLS を越える必要があるため、DB 側の関数に判定させる
 * (→ 20260813000008_protect_favorites.sql)。誰が入れたかは返ってこない。
 */
export async function fetchProtectedIds(mediaIds: string[]): Promise<Set<string>> {
  if (mediaIds.length === 0) return new Set();
  const response = await supabase.rpc("favorited_media_ids", { ids: mediaIds });
  if (response.error) throw response.error;
  const rows: unknown = response.data;
  if (!Array.isArray(rows)) return new Set();
  return new Set(rows.filter((row): row is string => typeof row === "string"));
}

export async function addFavorite(mediaId: string): Promise<void> {
  await addFavorites([mediaId]);
}

/**
 * まとめて入れる。既に入っているものは黙って飛ばす。
 * user_id は DB 側の既定値で auth.uid() が入る。
 */
export async function addFavorites(mediaIds: string[]): Promise<void> {
  if (mediaIds.length === 0) return;
  const { error } = await supabase.from("media_favorites").upsert(
    mediaIds.map((media_id) => ({ media_id })),
    { onConflict: "media_id,user_id", ignoreDuplicates: true },
  );
  if (error) throw error;
}

export async function removeFavorite(mediaId: string): Promise<void> {
  await removeFavorites([mediaId]);
}

export async function removeFavorites(mediaIds: string[]): Promise<void> {
  if (mediaIds.length === 0) return;
  const { error } = await supabase.from("media_favorites").delete().in("media_id", mediaIds);
  if (error) throw error;
}
